// src/pages/admin/AdminMarket.js
import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function AdminMarket() {
  const [prices, setPrices] = useState(null);
  const [logs, setLogs] = useState([]);
  const [priceForm, setPriceForm] = useState({ ccc_mandatory: '', ccc_voluntary: '' });

  useEffect(() => {
    api.get('/market/prices').then(r => setPrices(r.data)).catch(() => {});
    api.get('/admin/logs').then(r => setLogs(r.data.logs || [])).catch(() => {});
  }, []);

  const broadcastPrice = async () => {
    try {
      await api.post('/admin/broadcast-price', priceForm);
      toast.success('Reference market snapshot published');
      const r = await api.get('/market/prices');
      setPrices(r.data);
    } catch (err) { toast.error('Failed'); }
  };

  const readAllNotifs = async () => {
    await api.put('/admin/notifications/read-all');
    toast.success('All notifications marked read');
  };

  const chartData = prices?.history?.slice(-14).map((p, i) => ({
    day: `D${i + 1}`,
    mandatory: Math.round(p.ccc_mandatory),
    voluntary: Math.round(p.ccc_voluntary),
  })) || [];

  return (
    <div>
      <div className="section-tag">Admin</div>
      <h1 className="section-title">MARKET <span style={{ color: 'var(--neon-green)' }}>CONTROL</span></h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '2rem' }}>
        {/* PRICE BROADCAST */}
        <div className="card" style={{ borderTop: '2px solid var(--neon-green)' }}>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.3rem', letterSpacing: '2px', marginBottom: '1.5rem' }}>PUBLISH MARKET SNAPSHOT</div>
          <div className="form-group">
            <label className="form-label">CCC Mandatory Price (₹)</label>
            <input className="form-input" type="number" value={priceForm.ccc_mandatory} onChange={e => setPriceForm(p => ({ ...p, ccc_mandatory: e.target.value }))} placeholder={prices?.spot?.ccc_mandatory} />
          </div>
          <div className="form-group">
            <label className="form-label">CCC Voluntary Price (₹)</label>
            <input className="form-input" type="number" value={priceForm.ccc_voluntary} onChange={e => setPriceForm(p => ({ ...p, ccc_voluntary: e.target.value }))} placeholder={prices?.spot?.ccc_voluntary} />
          </div>
          <button className="btn btn-primary" onClick={broadcastPrice}>PUBLISH SNAPSHOT</button>
        </div>

        {/* CURRENT PRICES */}
        {prices && (
          <div className="card" style={{ borderTop: '2px solid var(--neon-cyan)' }}>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.3rem', letterSpacing: '2px', marginBottom: '1.5rem' }}>REFERENCE SPOT PRICES</div>
            {Object.entries(prices.spot).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontFamily: 'Space Mono, monospace', fontSize: '0.72rem' }}>
                <span style={{ color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>{k.replace(/_/g, ' ')}</span>
                <span style={{ color: 'var(--neon-green)' }}>₹{v.toLocaleString()}</span>
              </div>
            ))}
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={readAllNotifs}>Mark All Notifs Read</button>
            </div>
            <div style={{ marginTop: '0.8rem', fontFamily: 'Space Mono, monospace', fontSize: '0.62rem', color: 'var(--muted)' }}>
              {prices?.source?.label || 'Reference market snapshot'}
            </div>
          </div>
        )}
      </div>

      {/* PRICE CHART */}
      {chartData.length > 0 && (
        <div className="card" style={{ marginTop: '2rem' }}>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.3rem', letterSpacing: '2px', marginBottom: '1rem' }}>14-DAY PRICE HISTORY</div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <XAxis dataKey="day" tick={{ fontFamily: 'Space Mono', fontSize: 10, fill: '#5a5e72' }} />
              <YAxis tick={{ fontFamily: 'Space Mono', fontSize: 10, fill: '#5a5e72' }} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ background: '#0d0f1a', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'Space Mono', fontSize: '0.7rem' }} />
              <Line type="monotone" dataKey="mandatory" stroke="#00ff88" strokeWidth={2} dot={false} name="CCC Mandatory" />
              <Line type="monotone" dataKey="voluntary" stroke="#00e5ff" strokeWidth={2} dot={false} name="CCC Voluntary" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* AUDIT LOGS */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.3rem', letterSpacing: '2px', marginBottom: '1rem' }}>AUDIT LOGS</div>
        <div style={{ maxHeight: '300px', overflow: 'auto' }} className="thin-scroll">
          <table className="data-table">
            <thead><tr><th>Action</th><th>User</th><th>Details</th><th>Timestamp</th></tr></thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td><span className={`badge badge-${log.action.includes('KYC') ? 'green' : log.action.includes('SUSPEND') ? 'pink' : 'cyan'}`}>{log.action}</span></td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem' }}>{log.user_email || log.admin_id}</td>
                  <td style={{ color: 'var(--muted)', fontSize: '0.65rem' }}>{log.reason || log.target_user_id || '—'}</td>
                  <td style={{ color: 'var(--muted)', fontSize: '0.65rem' }}>{new Date(log.created_at).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
